<template>
  <b-row align-h="center" class="flex-grow-1">
    <b-col cols="auto" class="mb-5 text-center">
      <div class="btb-instruction my-4">{{ instruction }}</div>
      <b-button v-b-modal.modal-waiver variant="dark">{{ buttonText }}</b-button>
    </b-col>
    
    <section v-show="hasAgreed">
      <b-row align-h="center" class="mt-2 text-center">
        <b-col cols="12" sm="10" md="8">
          <VueSignaturePad
            class="btb-signature-pad" 
            ref="signaturePad" 
            :options="{ onBegin, onEnd }"
            v-bind="signaturePadDimensions"
          />
        </b-col>
      </b-row>
      <b-row align-h="center" class="my-4">
        <b-col cols="auto">
          <b-button type="button" variant="outline-secondary" @click="clearSignature">
            Clear Signature
          </b-button>
        </b-col>
      </b-row>
    </section>
    
    <b-modal :id="modal.id" 
      scrollable 
      :title="title"
      size="xl"
      ok-only
      :ok-disabled="!hasAgreed"
    >
      <template #modal-header-close>
        <b-icon icon="x-lg" />
      </template>

      <p class="mb-4" v-for="paragraph in getContract()"
        :key="paragraph"
        v-html="paragraph"
      />

      <label class="my-4">
        <input class="hidden"
          type="checkbox"
          v-model="formData.agreed" 
          :value="true"
        >
        <span class="btb-waiver-checkbox" />
        <span class="btb-waiver-acknowledge">{{ agreement }}</span>
      </label>
    </b-modal>
  </b-row>
</template>

<script>
import { mapState } from 'vuex';
import { MINORS, WAIVER_CONTRACT } from '@/store/state';
import { getSortedMediaQueriesAsTuples } from '@/utils/breakpoints';
import { queries } from '@/main'
import { FIND_OR_CREATE_PERSON } from '../../store/actions';
import { UPDATE_SIGNATURE } from '@/store/mutations';

export const CONTRACT_EVENTS = {
  COMPLETED: 'Contract:Completed'
}

const getSignaturePadDimensions = (mq) => {
  const shared = {
    width: '100%',
  }

  const opts = {
    sm: { height: '150px' },
    md: { height: '250px' },
    lg: { height: '400px' },
  }

  const calculated = opts[mq] ?? { height: `30vh` }

  return Object.assign({}, shared, opts[mq], calculated);
}

export default {
  name: 'Contract',
  inject: ['mediaQueries'],
  data () {
    return {
      agreement: 'I have read the above waiver and release and by signing it agree it is my intention to exempt and relieve The Company from liability for personal injury, property damage and/or wrongful death caused by negligence and/or any other cause.',
      buttonText: 'View Full Waiver',
      formData: {
        agreed: false,
        signature: null,
      },
      instruction: 'Please click to review and sign the full waiver',
      modal: {
        id: 'modal-waiver'
      },
      signatureOptions: {
        penColor: "black"
      },
      title: 'Beat The Bomb Waiver',
      formSubmitted: false
    }
  },
  computed: {
    ...mapState([
      MINORS,
      WAIVER_CONTRACT
    ]),
    canProceed () {
      return this.hasAgreed && this.formData.signature
    },
    hasAgreed () {
      return this.formData.agreed;
    },
    signaturePadDimensions () {
      // TODO: refactor to just use vh units
      const sortedQueries = getSortedMediaQueriesAsTuples(queries, 'desc');
      const largestBreakpointKey = sortedQueries.find(([ key ]) => this.mediaQueries[key]);
      
      return getSignaturePadDimensions(largestBreakpointKey);
    },
  },
  mounted () {
    if(this.canProceed) this.completeStep(true)
  },
  watch: {
    canProceed (fresh) {
      this.completeStep(fresh)
    },
  },
  methods: {
    clearSignature () {
      this.$refs.signaturePad.clearSignature();
      this.$store.commit(UPDATE_SIGNATURE, undefined)
      this.formData.signature = undefined
    },
    completeStep (isComplete) {
      this.$emit(CONTRACT_EVENTS.COMPLETED, (isComplete) ? this : null);
    },
    onBegin () {
      // BUG: https://github.com/neighborhood999/vue-signature-pad/issues/62
      this.$refs.signaturePad.resizeCanvas();
    },
    onEnd () {
      const { isEmpty, data } = this.$refs.signaturePad.saveSignature();
      if (!isEmpty) {
        this.formData.signature = data;
        this.$store.commit(UPDATE_SIGNATURE, data);
      }
    },
    getContract(){
      let paragraphs = [...this[WAIVER_CONTRACT]];
      if (this[MINORS] != null && this[MINORS].length > 0){
        var minorList = [];
        this[MINORS].forEach(minor => {
          if (minor != null && minor.first_name != null && minor.first_name != ""){
            minorList.push(minor);
          }
        });

        if (minorList.length == 0) return paragraphs;

        var minorListString = `I also take responsibility for these minors below and agree to the terms above for them:`+'<br>';
        minorList.forEach(minor => {
          minorListString += ` ${minor.first_name} ${minor.last_name} ${minor.date_of_birth}`+'<br>';
        });
        
        minorListString = minorListString.substring(0, minorListString.length - 1);
        paragraphs.push(minorListString);
      }
      return paragraphs;
    }
  },
  beforeRouteLeave (to, from, next) {
    this.formSubmitted = true
    if (this.canProceed || to.name === 'Guardian') {
      next()
    }
    return next(false)
  }
}
</script>

<style>
.hidden {
  visibility: hidden;
  position: absolute;
}

.btb-instruction {
  font-size: 1.5rem;
}

.btb-waiver-checkbox {
  float: left;
  border: 1px solid var(--bs-danger);
  border-radius: 4px;
  margin-top: 2px;
  margin-right: 6px;
  vertical-align: middle;
  width: 1.5rem;
  height: 1.5rem;

  position: relative;
}

[type='checkbox']:checked ~ .btb-waiver-checkbox {
  border-color: var(--bs-primary);
  background-color: var(--bs-primary);
}

[type='checkbox']:checked ~ .btb-waiver-checkbox::after {
  content: '\2713';
  position: absolute;
  top: 50%;
  left: 0;
  transform: translateY(-50%);
  font-size: 1.5rem;
  color: var(--bs-white);
}

.btb-waiver-acknowledge {
  font-weight: 600;
  font-size: 1.2rem;
}

.btb-signature-pad {
  border: double 3px transparent;
  border-radius: 4px;
  background-image: linear-gradient(white, white),
    radial-gradient(circle at top left, #4bc5e8, #9f6274);
  background-origin: border-box;
  background-clip: content-box, border-box;
}
</style>